import { Children, isValidElement, useState, type ReactNode } from "react";
import { Icon, type IconName } from "@/components/common/Icon";
import { StatusDot, type StatusDotTone } from "@/components/common/StatusDot";
import { Spinner } from "@/components/common/Spinner";
import { ROW_HOVER_CLASS } from "@/lib/row-styles";

/** エージェントのカードの外枠。先頭に ResourceRow を置き、チャット一覧などを続ける。ホバー中は地と枠を濃くする。 */
export function ResourceCard({ testId, children }: { testId?: string; children: ReactNode }) {
	const [hovered, setHovered] = useState(false);
	return (
		<div
			data-testid={testId}
			onPointerEnter={() => setHovered(true)}
			onPointerLeave={() => setHovered(false)}
			style={{
				display: "flex",
				flexDirection: "column",
				// grid に並べたとき行の高さまで引き伸ばされ、下に空白ができないようにする。
				alignSelf: "start",
				// 幅は並べる grid の列に任せ、中身に押し広げられず列の幅まで縮むようにする。
				minWidth: 0,
				boxSizing: "border-box",
				background: hovered ? "var(--surface-2)" : "var(--surface-1)",
				boxShadow: hovered ? "var(--shadow-ring-strong)" : "var(--shadow-card)",
				borderRadius: "var(--radius-5)",
				transition: "background 120ms ease, box-shadow 120ms ease",
			}}
		>
			{children}
		</div>
	);
}

/** 一覧の幅の上限。エージェントのカードと同じ幅に揃え、行が横に間延びしないようにする。 */
export const RESOURCE_LIST_MAX_WIDTH = 700;

/**
 * 共有ボリューム・シークレットのように同種のものを 1 つの枠にまとめ、行どうしを横線で区切って並べる。
 * 行には `compact` な ResourceRow を置く。
 */
export function ResourceList({ maxWidth, children }: { maxWidth?: number; children: ReactNode }) {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				maxWidth,
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-card)",
				borderRadius: "var(--radius-5)",
				// ホバーした先頭・末尾の行の地が角からはみ出さないようにする。
				overflow: "hidden",
			}}
		>
			{Children.toArray(children).map((child, index) => (
				<ResourceListItem key={isValidElement(child) ? child.key : index} first={index === 0}>
					{child}
				</ResourceListItem>
			))}
		</div>
	);
}

/** ResourceList の 1 行の枠。 */
function ResourceListItem({ first, children }: { first: boolean; children: ReactNode }) {
	return (
		<div
			className={`${ROW_HOVER_CLASS} transition-colors`}
			style={{ borderTop: first ? 0 : "var(--pane-header-border)" }}
		>
			{children}
		</div>
	);
}

/**
 * 1 件分の行。左にアイコンのタイル (`icon` を渡したときだけ)、中央に名前 (と横に並べるタグ・状態) とその下の補足行、右に操作ボタンを置く。
 * `children` は補足行の下に出す (明細やエラーの説明など)。
 */
export function ResourceRow({
	testId,
	icon,
	compact = false,
	title,
	tags,
	meta,
	actions,
	children,
}: {
	testId?: string;
	icon?: IconName;
	/** ResourceList に並べる行向けに余白を詰める。 */
	compact?: boolean;
	title: ReactNode;
	tags?: ReactNode;
	meta?: ReactNode;
	actions?: ReactNode;
	children?: ReactNode;
}) {
	return (
		<div
			data-testid={testId}
			style={{
				display: "flex",
				alignItems: "flex-start",
				gap: 12,
				padding: compact ? "12px 16px" : 16,
			}}
		>
			{icon && (
				<span
					style={{
						display: "grid",
						placeItems: "center",
						width: 40,
						height: 40,
						flexShrink: 0,
						marginTop: 2,
						borderRadius: "var(--radius-5)",
						background: "var(--surface-2)",
						boxShadow: "var(--shadow-ring)",
						color: "var(--fg-2)",
					}}
				>
					<Icon name={icon} size={20} />
				</span>
			)}
			<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
				<div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
					{title}
					{tags}
				</div>
				{meta && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							minWidth: 0,
							fontSize: "var(--text-caption)",
							color: "var(--fg-3)",
						}}
					>
						{meta}
					</div>
				)}
				{children}
			</div>
			{actions && (
				<div
					style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0, marginRight: -7 }}
				>
					{actions}
				</div>
			)}
		</div>
	);
}

/** 名前。長い名前は省略し、ツールチップで全体を出す。 */
export function ResourceTitle({
	compact = false,
	mono = false,
	children,
}: {
	compact?: boolean;
	mono?: boolean;
	children: string;
}) {
	return (
		<span
			style={{
				minWidth: 0,
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				// 等幅は横に広く重く見えるため、一段小さくする。
				fontSize: mono ? "var(--text-ui)" : "var(--text-title)",
				fontFamily: mono ? "var(--font-mono)" : undefined,
				fontWeight: compact ? 500 : 600,
				color: "var(--fg-1)",
			}}
			title={children}
			data-tooltip-if-truncated
		>
			{children}
		</span>
	);
}

/** 名前の横や明細に並べる、色を持たない補足のタグ (インスタンスタイプ・許可ホストなど)。 */
export function ResourceTag({
	testId,
	mono = false,
	children,
}: {
	testId?: string;
	mono?: boolean;
	children: string;
}) {
	return (
		<span
			data-testid={testId}
			style={{
				flexShrink: 0,
				padding: "1px 8px",
				borderRadius: "var(--radius-full)",
				background: "var(--overlay-hover)",
				fontSize: "var(--text-caption)",
				fontFamily: mono ? "var(--font-mono)" : undefined,
				fontWeight: 500,
				color: "var(--fg-2)",
			}}
		>
			{children}
		</span>
	);
}

/** 横に並べた項目どうしを区切る中黒。 */
export function MetaSeparator() {
	return <span style={{ flexShrink: 0, color: "var(--fg-4)" }}>·</span>;
}

/**
 * 色付きの丸と文言で状態を示す。`indicator` が `ping` なら稼働中の波紋、`pulse` なら遷移中の明滅、
 * `spinner` なら状態の取得中を示す。
 */
export function ResourceStatus({
	tone,
	indicator = "dot",
	tooltip,
	testId,
	children,
}: {
	tone: StatusDotTone;
	indicator?: "dot" | "ping" | "pulse" | "spinner";
	tooltip?: string;
	testId?: string;
	children?: ReactNode;
}) {
	return (
		<span
			data-testid={testId}
			data-tooltip={tooltip}
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 6,
				flexShrink: 0,
				fontSize: "var(--text-caption)",
				fontWeight: 500,
				color: TONE_COLOR[tone],
			}}
		>
			{indicator === "spinner" ? (
				<Spinner size={10} color={TONE_COLOR[tone]} />
			) : (
				<StatusDot tone={tone} ping={indicator === "ping"} pulse={indicator === "pulse"} />
			)}
			{children}
		</span>
	);
}

const TONE_COLOR: Record<StatusDotTone, string> = {
	info: "var(--accent)",
	success: "var(--success)",
	warning: "var(--warning)",
	danger: "var(--danger)",
	muted: "var(--fg-3)",
	disabled: "var(--fg-4)",
};

/** 行の中に並べる明細の 1 項目。ラベルの右に値を置く。 */
export function ResourceDetail({ label, children }: { label: string; children: ReactNode }) {
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				flexWrap: "wrap",
				gap: 6,
				minWidth: 0,
				fontSize: "var(--text-caption)",
			}}
		>
			<span style={{ flexShrink: 0, fontWeight: 500, color: "var(--fg-3)" }}>{label}</span>
			{children}
		</span>
	);
}

/** ResourceDetail を折り返しながら横に並べる。 */
export function ResourceDetails({ children }: { children: ReactNode }) {
	return (
		<div style={{ display: "flex", flexWrap: "wrap", columnGap: 16, rowGap: 4, paddingTop: 6 }}>
			{children}
		</div>
	);
}
